/** @jsx isolateComponent */
import { Fragment, useState } from 'react';

import { Avatar } from '../../display/Avatar';
import { Link } from '../../navigation/Link';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Img } from '../../display/Img';
import { css } from '@emotion/core';
import { Animation, Colors, Typography } from '../../../theme';
import {
  DateTime,
  formatDateTime,
  getClientTimeZone,
  useDate,
} from '@zapier/date';
import type { Dateable } from '@zapier/date';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

type Author = {
  name: string;
  image?: string;
};

type Props = {
  /** The article image URL */
  imageUrl: string;

  /** The category the article is assigned */
  category?: string;

  /** The read time expressed in minutes and seconds, e.g. 5:30 */
  readTimeMinutes?: string;

  /** Title of the article */
  title: string;

  /** When this component is clicked, the user will be taken to this URL */
  url: string;

  /** The information for the author of the article */
  author: Author;

  /** The date the article was published */
  publishedDate: Dateable;
};

const Styles = labelStyles('Article', {
  content: css({
    margin: '0 10px',
    display: 'flex',
    flexDirection: 'column',
  }),
  category: css({
    ...Typography.allCaps800,
    color: Colors.grey700,
    lineHeight: '20px',
  }),
  authorName: {
    ...Typography.altText800,
    color: Colors.grey700,
    fontWeight: 700,
    lineHeight: '20px',
  },
  minRead: { ...Typography.altText900, color: Colors.grey700 },
  date: { ...Typography.altText800, color: Colors.grey700, lineHeight: '20px' },
  articleImage: {
    marginBottom: 10,
  },
  articleTitle: (isHovered: boolean) => {
    return [
      {
        ...Typography.subtitle700,
        color: Colors.black,
        marginBottom: 10,
        order: 2,
        transitionDuration: Animation.transitionDuration,
        transitionProperty: 'color',
        transitionTimingFunction: Animation.transitionTimingFunction,
      },
      isHovered &&
        withModifier('isHovered', {
          color: Colors.blue500,
        }),
    ];
  },
  articleAuthor: {
    order: 3,
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '30px auto',
    gap: 10,
  },
  articleMetadata: {
    order: 1,
    marginBottom: 5,
  },
});

const ArticleMetadata = ({
  readTimeMinutes,
  category,
}: {
  readTimeMinutes?: string;
  category?: string;
}) => {
  return (
    <div css={Styles.articleMetadata}>
      {category && (
        <Fragment>
          <span css={Styles.category}>
            {category} {readTimeMinutes ? '·' : null}{' '}
          </span>{' '}
        </Fragment>
      )}
      {readTimeMinutes && (
        <span css={Styles.minRead}>
          <span>{readTimeMinutes} min read</span>
        </span>
      )}
    </div>
  );
};

const ArticleAuthor = ({
  author,
  publishedDate,
}: {
  author: Author;
  publishedDate: Dateable;
}) => {
  const { name, image } = author;
  return (
    <div css={Styles.articleAuthor}>
      <Avatar name={name} url={image} />
      <div>
        <span css={Styles.authorName}>{name} · </span>{' '}
        <span css={Styles.date}>
          <DateTime date={publishedDate} />
        </span>
      </div>
    </div>
  );
};

/**
 * Renders an article preview which contains a title, image, and publishing
 * information: date, category, and author details.
 */
export const Article = (props: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    author,
    category,
    imageUrl,
    publishedDate,
    readTimeMinutes,
    title,
    url,
  } = props;
  const { timezone } = useDate();
  const clientTimezone = timezone || getClientTimeZone() || 'UTC';
  const formattedDate = formatDateTime(
    publishedDate,
    'default',
    clientTimezone
  );
  const [minutes, seconds] = (readTimeMinutes || '').split(':');
  const ariaLabelReadtime =
    minutes && seconds
      ? `Estimated read time: ${minutes} minutes, ${seconds} seconds.`
      : '';
  const ariaLabelCategory = category ? `Categorized under ${category}.` : '';
  const ariaLabel = `${title} Written by ${author.name}.
  Published ${formattedDate}. ${ariaLabelCategory} ${ariaLabelReadtime}`;
  return (
    <div
      onBlur={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      onMouseOver={() => setIsHovered(true)}
    >
      <Link ariaLabel={ariaLabel} href={url}>
        <div css={Styles.articleImage}>
          <Img
            alt={`Image for the article titled ${title}`} // `aria-hidden` is applied here since we do not describe our images
            // for screen readers. Understanding the content of the image is not
            // critical to understanding the content of the article.
            ariaHidden={true}
            aspectRatio="16:9"
            height={220}
            objectFit="cover"
            shadow={isHovered ? 'elevation20' : 'elevation5'}
            src={imageUrl}
          />
        </div>
        {/*
         The content is order sensitive. It is reorganized using flexbox with
         the `order` prop. See the styles above for how they are organized.
         This allows screen readers to read it in the order of Title ->
         Publishing information -> Metadata.
         Another note here is that the `<article>` element wraps this content
         and not the entire `<Article>` React component.
         This fixes an issue where VoiceOver ignores the `order` of the items
         and reads it in the order of the `order` CSS property.
        */}
        <article css={Styles.content}>
          <HeadingLevel>
            <Heading css={Styles.articleTitle(isHovered)}>{title}</Heading>
          </HeadingLevel>
          <ArticleAuthor author={author} publishedDate={publishedDate} />
          <ArticleMetadata
            category={category}
            readTimeMinutes={readTimeMinutes}
          />
        </article>
      </Link>
    </div>
  );
};
